import React, { useState, useEffect, useRef } from "react";
import {
  MapPin,
  Users,
  ArrowRight,
  ArrowUpDown,
  ChevronDown,
  DollarSign,
} from "lucide-react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Slider,
  Switch,
  CircularProgress,
} from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import { API_BASE_URL, authenticatedFetch } from "../api.js";
import { useNavigate } from "react-router-dom";
import { usePostHog, getLocationLabel } from "../Posthogprovider.jsx";
import DemoSpotlight from "./DemoSpotlight";

const Container = styled(Paper)(({ theme }) => ({
  width: "100%",
  height: "100dvh", // Use dynamic viewport height
  margin: "0 auto",
  boxShadow: theme.shadows[6],
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  position: "relative",
  overscrollBehavior: "none",
}));

const Header = styled(Box)(({ theme }) => ({
  background: "linear-gradient(90deg, #f97316, #ef4444)",
  color: "#fff",
  padding: theme.spacing(3),
  position: "sticky",
  top: 0,
  zIndex: 10,
}));

const HeaderRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(1),
}));

const HeaderText = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
}));

const HeaderTitle = styled(Typography)(() => ({
  fontWeight: 700,
}));

const HeaderSubtitle = styled(Typography)(() => ({
  color: "rgba(255,255,255,0.85)",
}));

const NextButton = styled(Button)(({ theme }) => ({
  paddingInline: theme.spacing(2.5),
  paddingBlock: theme.spacing(1.25),
  borderRadius: theme.spacing(2),
  fontWeight: 700,
  fontSize: "0.8rem",
  boxShadow: theme.shadows[4],
  textTransform: "none",
  backgroundColor: "#fff",
  color: "#f97316",
  "&:hover": {
    backgroundColor: "#fff",
    boxShadow: theme.shadows[8],
  },
  "&.Mui-disabled": {
    backgroundColor: "rgba(255,255,255,0.3)",
    color: "rgba(255,255,255,0.5)",
    boxShadow: theme.shadows[1],
  },
  "&.starting": {
    animation: `${startingPulse} 1.4s ease-in-out infinite`,
  },
}));

const startingPulse = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
  }
  50% {
    transform: scale(1.03);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
  }
`;

const NextButtonContent = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

const Content = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  paddingBottom: `calc(${theme.spacing(
    3,
  )} + env(safe-area-inset-bottom, 20px))`,
  flex: 1,
  overflowY: "auto",
  overscrollBehavior: "contain",
  WebkitOverflowScrolling: "touch",
}));

const Section = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const SectionLabel = styled(Typography)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  fontWeight: 600,
  color: theme.palette.grey[800],
  marginBottom: theme.spacing(1.5),
}));

const LocationSuggestions = styled(Paper)(({ theme }) => ({
  marginTop: theme.spacing(1),
  borderRadius: theme.spacing(2),
  maxHeight: 240,
  overflowY: "auto",
  border: `1px solid ${theme.palette.grey[200]}`,
}));

const SuggestionList = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
}));

const SuggestionItem = styled("button")(({ theme }) => ({
  width: "100%",
  textAlign: "left",
  padding: theme.spacing(1.5, 2),
  border: "none",
  background: "transparent",
  cursor: "pointer",
  borderBottom: "1px solid #f3f4f6",
  "&:last-of-type": {
    borderBottom: "none",
  },
  "&:hover": {
    backgroundColor: "#fff7ed",
  },
}));

const SuggestionText = styled(Typography)(() => ({
  fontWeight: 500,
  color: "#111827",
  fontSize: "0.9rem",
}));

const LocationSetCard = styled(Box)(({ theme }) => ({
  backgroundColor: "#ecfdf3",
  borderRadius: theme.spacing(2),
  padding: theme.spacing(2),
  border: "1px solid #bbf7d0",
}));

const LocationSetRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

const LocationSetInfo = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
}));

const LocationIconWrapper = styled(Box)(({ theme }) => ({
  width: 40,
  height: 40,
  borderRadius: "999px",
  backgroundColor: "#22c55e",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
}));

const LocationSetTitle = styled(Typography)(() => ({
  fontWeight: 700,
  color: "#14532d",
}));

const LocationSetSubtitle = styled(Typography)(() => ({
  color: "#15803d",
}));

const LocationResetButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(1.5),
  borderRadius: theme.spacing(1.5),
  textTransform: "none",
  fontWeight: 500,
  borderColor: "#86efac",
  color: "#166534",
  backgroundColor: "#fff",
  "&:hover": {
    backgroundColor: "#f0fdf4",
  },
}));

const PriceGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: theme.spacing(1.5),
}));

const PriceButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "active",
})(({ theme, active }) => ({
  borderRadius: theme.spacing(2),
  paddingBlock: theme.spacing(1.25),
  fontWeight: 700,
  textTransform: "none",
  backgroundColor: active ? "#f97316" : "rgba(243,244,246,1)",
  color: active ? "#fff" : "#4b5563",
  boxShadow: active ? theme.shadows[4] : "none",
  transform: active ? "scale(1.03)" : "none",
  borderColor: active ? "#f97316" : "transparent",
  "&:hover": {
    backgroundColor: active ? "#fb923c" : "#e5e7eb",
  },
}));

// Segmented Control Components
const SegmentedControl = styled(Box)(({ theme }) => ({
  display: "flex",
  backgroundColor: "#f3f4f6",
  borderRadius: theme.spacing(2),
  padding: theme.spacing(0.5),
}));

const SegmentButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "active",
})(({ theme, active }) => ({
  flex: 1,
  paddingBlock: theme.spacing(1),
  fontSize: "0.875rem",
  fontWeight: 600,
  textTransform: "none",
  backgroundColor: active ? "#fff" : "transparent",
  color: active ? "#f97316" : "#6b7280",
  boxShadow: active ? theme.shadows[2] : "none",
  borderRadius: theme.spacing(1.5),
  minWidth: 0,
  "&:hover": {
    backgroundColor: active ? "#fff" : "rgba(0,0,0,0.05)",
  },
}));

// NEW: Demo Button Styled Component
const DemoButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "active",
})(({ theme, active }) => ({
  flex: 1,
  paddingBlock: theme.spacing(1),
  fontSize: "0.875rem",
  fontWeight: 600,
  textTransform: "none",
  backgroundColor: active ? "#fff" : "transparent",
  color: active ? "#f97316" : "#6b7280",
  boxShadow: active ? theme.shadows[2] : "none",
  borderRadius: theme.spacing(1.5),
  minWidth: 0,
  "&:hover": {
    backgroundColor: active ? "#fff" : "rgba(0,0,0,0.05)",
  },
}));

const RadiusHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: theme.spacing(1),
}));

const RadiusLabel = styled(Typography)(() => ({
  fontWeight: 600,
}));

const RadiusValue = styled(Typography)(() => ({
  fontWeight: 600,
  color: "#f97316",
}));

const RadiusLabels = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  fontSize: "0.75rem",
  color: theme.palette.grey[500],
  marginTop: theme.spacing(-1.5),
}));

const ToggleCard = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(2),
  gap: theme.spacing(2),
  borderRadius: theme.spacing(2),
  border: `1px solid ${theme.palette.grey[200]}`,
  backgroundColor: theme.palette.grey[50],
}));

const ToggleText = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
}));

const ShopEmoji = styled("span")(() => ({
  fontSize: "1.5rem",
}));

const ToggleTitle = styled(Typography)(() => ({
  fontWeight: 600,
}));

const ToggleSubtitle = styled(Typography)(() => ({}));

const FoodCategoriesHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginBottom: theme.spacing(1),
}));

const FoodCategoriesToggle = styled(Button)(({ theme }) => ({
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: 0,
  paddingBottom: theme.spacing(1),
  textTransform: "none",
  marginBottom: theme.spacing(1.5),
  "&:hover": {
    backgroundColor: "transparent",
  },
}));

const FoodCategoriesToggleLeft = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
}));

const FoodCategoriesToggleTitle = styled(Typography)(() => ({
  fontWeight: 600,
  color: "#1f2937",
}));

const FoodCategoriesToggleSubtitle = styled(Typography)(() => ({
  color: "#6b7280",
}));

const FoodCategoriesToggleIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isOpen",
})(({ isOpen }) => ({
  display: "flex",
  alignItems: "center",
  transition: "transform 0.2s ease",
  transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
}));

const FoodCategoriesContent = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isOpen",
})(({ theme, isOpen }) => ({
  maxHeight: isOpen ? "1000px" : "0",
  overflow: "hidden",
  transition: "max-height 0.3s ease",
}));

const FoodCategoriesLabel = styled(Typography)(() => ({
  fontWeight: 600,
}));

const FoodCategoriesHint = styled(Typography)(({ theme }) => ({
  marginLeft: theme.spacing(1),
  color: theme.palette.text.secondary,
}));

const NoteBox = styled(Box)(({ theme }) => ({
  backgroundColor: "#eff6ff",
  borderRadius: theme.spacing(2),
  padding: theme.spacing(1.5),
  border: "1px solid #bfdbfe",
  marginBottom: theme.spacing(1.5),
}));

const NoteText = styled(Typography)(() => ({
  color: "#1d4ed8",
}));

const CategoriesGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: theme.spacing(1),
}));

const CategoryButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "selected",
})(({ theme, selected }) => ({
  borderRadius: theme.spacing(2),
  paddingBlock: theme.spacing(1.25),
  justifyContent: "flex-start",
  textTransform: "none",
  fontWeight: 500,
  gap: theme.spacing(1.5),
  backgroundColor: selected ? "#f97316" : "#f3f4f6",
  color: selected ? "#fff" : "#374151",
  borderColor: selected ? "#f97316" : "transparent",
  boxShadow: selected ? theme.shadows[4] : "none",
  transform: selected ? "scale(1.02)" : "none",
  "&:hover": {
    backgroundColor: selected ? "#fb923c" : "#e5e7eb",
  },
}));

const Emoji = styled("span")(() => ({
  fontSize: "1.25rem",
}));

const CategoryLabel = styled(Typography)(() => ({
  fontSize: "0.9rem",
}));

const UseCurrentLocationButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(1),
  padding: 0,
  minWidth: 0,
  textTransform: "none",
  fontWeight: 500,
  color: "#f97316",
}));

// Category definitions
const foodCategories = [
  {
    key: "american",
    id: [
      "4bf58dd8d48988d14e941735",
      "4bf58dd8d48988d147941735",
      "4bf58dd8d48988d16c941735",
      "4bf58dd8d48988d1cc941735",
      "4bf58dd8d48988d14f941735",
      "4bf58dd8d48988d157941735",
      "4bf58dd8d48988d155941735",
      "4bf58dd8d48988d11b941735",
      "4bf58dd8d48988d116941735",
    ],
    label: "American",
    emoji: "🍔",
  },
  {
    key: "italian",
    id: ["4bf58dd8d48988d110941735", "4bf58dd8d48988d1ca941735"],
    label: "Italian",
    emoji: "🍝",
  },
  {
    key: "mexican",
    id: [
      "4bf58dd8d48988d1c1941735",
      "4bf58dd8d48988d151941735",
      "4bf58dd8d48988d153941735",
    ],
    label: "Mexican",
    emoji: "🌮",
  },
  {
    key: "chinese",
    id: ["4bf58dd8d48988d145941735"],
    label: "Chinese",
    emoji: "🥡",
  },
  {
    key: "japanese",
    id: ["4bf58dd8d48988d111941735", "4bf58dd8d48988d1d2941735"],
    label: "Japanese",
    emoji: "🍣",
  },
  {
    key: "indian",
    id: ["4bf58dd8d48988d10f941735"],
    label: "Indian",
    emoji: "🍛",
  },
  {
    key: "thai",
    id: ["4bf58dd8d48988d149941735"],
    label: "Thai",
    emoji: "🍜",
  },
  {
    key: "mediterranean",
    id: [
      "4bf58dd8d48988d1c0941735",
      "4bf58dd8d48988d10e941735",
      "4bf58dd8d48988d115941735",
      "4bf58dd8d48988d10b941735",
    ],
    label: "Mediterranean",
    emoji: "🥙",
  },
  {
    key: "seafood",
    id: ["4bf58dd8d48988d1ce941735"],
    label: "Seafood",
    emoji: "🦞",
  },
  {
    key: "steakhouse",
    id: ["4bf58dd8d48988d1cc941735"],
    label: "Steakhouse",
    emoji: "🥩",
  },
  {
    key: "bbq",
    id: [
      "4bf58dd8d48988d1df931735",
      "4bf58dd8d48988d14f941735",
      "4bf58dd8d48988d14e941735",
    ],
    label: "BBQ",
    emoji: "🍖",
  },
  {
    key: "pizza",
    id: ["4bf58dd8d48988d1ca941735", "4bf58dd8d48988d110941735"],
    label: "Pizza",
    emoji: "🍕",
  },
  {
    key: "vegetarian",
    id: ["4bf58dd8d48988d1d3941735"],
    label: "Vegetarian",
    emoji: "🥗",
  },
  {
    key: "cafe",
    id: [
      "4bf58dd8d48988d16d941735",
      "4bf58dd8d48988d1e0931735",
      "4bf58dd8d48988d179941735",
      "4bf58dd8d48988d16a941735",
    ],
    label: "Cafe",
    emoji: "☕",
  },
  {
    key: "dessert",
    id: [
      "4bf58dd8d48988d1d0941735",
      "4bf58dd8d48988d1c9941735",
      "4bf58dd8d48988d1bc941735",
      "52f2ab2ebcbc57f1066b8b31",
    ],
    label: "Dessert",
    emoji: "🍰",
  },
  {
    key: "breakfast",
    id: [
      "4bf58dd8d48988d143941735",
      "4bf58dd8d48988d179941735",
      "4bf58dd8d48988d16a941735",
      "4bf58dd8d48988d16d941735",
      "4bf58dd8d48988d1e0931735",
      "4bf58dd8d48988d147941735",
      "4bf58dd8d48988d148941735",
    ],
    label: "Breakfast",
    emoji: "🥞",
  },
];

const sortOptions = [
  { key: "distance", label: "Distance" },
  { key: "rating", label: "Rating" },
  { key: "popularity", label: "Popularity" },
];

const priceOptions = [
  { key: "1", label: "$", description: "Budget" },
  { key: "2", label: "$$", description: "Moderate" },
  { key: "3", label: "$$$", description: "Pricey" },
  { key: "4", label: "$$$$", description: "Splurge" },
];

const buildFsqCategoryIds = (selectedKeys) => {
  const ids = [];
  foodCategories.forEach((cat) => {
    if (selectedKeys.includes(cat.key)) {
      if (Array.isArray(cat.id)) {
        ids.push(...cat.id);
      } else {
        ids.push(cat.id);
      }
    }
  });
  return ids;
};

// Cookie helper functions
const setCookie = (name, value, days) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
};

const getCookie = (name) => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

const Setup = (props) => {
  const { sessionInfo, setSessionInfo } = props;

  const [location, setLocation] = useState("");
  const [locationSet, setLocationSet] = useState(false);
  const [locationMethod, setLocationMethod] = useState("");
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [sortBy, setSortBy] = useState("distance");
  const [partySize, setPartySize] = useState(() => {
    // Default to demo (1) if user hasn't seen it, otherwise default to 2
    const demoSeen = getCookie("foodie_call_demo_seen");
    return demoSeen === "true" ? "2" : "1";
  });
  const [distance, setDistance] = useState("5");
  const [excludeChains, setExcludeChains] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [showFoodCategories, setShowFoodCategories] = useState(false);
  const [selectedPrices, setSelectedPrices] = useState(["1", "2", "3", "4"]);
  const [openNow, setOpenNow] = useState(true);
  const [hasSeenDemo, setHasSeenDemo] = useState(false);
  const [demoButtonPosition, setDemoButtonPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    height: 0,
  });
  const navigate = useNavigate();
  const posthog = usePostHog();
  const demoButtonRef = useRef(null);

  const LocationIQApiKey = process.env.REACT_APP_LOCATION_IQ_API_KEY;

  // Check if user has seen demo on component mount
  useEffect(() => {
    const demoSeen = getCookie("foodie_call_demo_seen");
    setHasSeenDemo(demoSeen === "true");
  }, []);

  useEffect(() => {
    posthog.capture("setup_page_viewed");
  }, [posthog]);

  useEffect(() => {
    const DEFAULT_MILES = 5;
    const DEFAULT_RADIUS_METERS = DEFAULT_MILES * 1610;

    setSessionInfo((prev) => {
      const safePrev = prev || {};
      return {
        ...safePrev,
        radius:
          safePrev.radius !== undefined
            ? safePrev.radius
            : DEFAULT_RADIUS_METERS,
        party_size:
          safePrev.party_size !== undefined ? safePrev.party_size : partySize,
        exclude_all_chains:
          safePrev.exclude_all_chains !== undefined
            ? safePrev.exclude_all_chains
            : false,
        sort_by: safePrev.sort_by !== undefined ? safePrev.sort_by : "distance",
        min_price: safePrev.min_price !== undefined ? safePrev.min_price : 1,
        max_price: safePrev.max_price !== undefined ? safePrev.max_price : 4,
        open_now: safePrev.open_now !== undefined ? safePrev.open_now : true,
        fsq_category_ids:
          Array.isArray(safePrev.fsq_category_ids) &&
          safePrev.fsq_category_ids.length > 0
            ? safePrev.fsq_category_ids
            : [],
      };
    });
  }, [setSessionInfo, partySize]);

  // Track demo button position for spotlight
  useEffect(() => {
    const updateButtonPosition = () => {
      if (demoButtonRef.current) {
        const rect = demoButtonRef.current.getBoundingClientRect();
        setDemoButtonPosition({
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
        });
      }
    };

    updateButtonPosition();
    window.addEventListener("resize", updateButtonPosition);
    window.addEventListener("scroll", updateButtonPosition);

    return () => {
      window.removeEventListener("resize", updateButtonPosition);
      window.removeEventListener("scroll", updateButtonPosition);
    };
  }, [partySize, hasSeenDemo]);

  const updateLatLon = (lat, lon) => {
    setLatitude(lat);
    setLongitude(lon);
    if (lat !== null && lon !== null) {
      setSessionInfo((prev) => ({
        ...(prev || {}),
        ll: `${lat},${lon}`,
      }));
    }
  };

  const clearLlFromSessionInfo = () => {
    setSessionInfo((prev) => {
      const next = { ...(prev || {}) };
      delete next.ll;
      return next;
    });
  };

  const handleUseCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const lat = position.coords.latitude;
          const lon = position.coords.longitude;

          setLocation(`${lat},${lon}`);
          updateLatLon(lat, lon);
          setLocationSet(true);
          setLocationMethod("current");
          setShowSuggestions(false);
          setSuggestions([]);

          const locationLabel = await getLocationLabel(lat, lon);
          posthog.capture("location_set", {
            method: "current",
            location_city: locationLabel,
          });
        },
        () => {
          alert("Unable to get your location. Please enter manually.");
          posthog.capture("location_permission_denied");
        },
      );
    } else {
      alert("Geolocation is not supported by your browser.");
    }
  };

  // this is for handling user searches in locationiq autocomplete
  const abortControllerRef = useRef(null);
  const debounceTimerRef = useRef(null);

  const handleLocationSearch = async (value) => {
    // Update UI state immediately (no debounce for this)
    setLocation(value);
    setLocationSet(false);
    setLocationMethod("");
    setLatitude(null);
    setLongitude(null);
    clearLlFromSessionInfo();

    // Clear existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Cancel previous API request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    if (value.length > 2) {
      // Debounce the API call
      debounceTimerRef.current = setTimeout(async () => {
        // Create new abort controller for this request
        const currentController = new AbortController();
        abortControllerRef.current = currentController;
        const signal = currentController.signal;

        try {
          const isLikelyZipCode = /^\d{3,5}(-\d{1,4})?$/.test(value.trim());
          let allResults = [];

          if (isLikelyZipCode) {
            try {
              const zipResponse = await fetch(
                `https://us1.locationiq.com/v1/search?key=${LocationIQApiKey}&postalcode=${encodeURIComponent(
                  value,
                )}&countrycodes=us&format=json`,
                { signal },
              );
              const zipData = await zipResponse.json();
              if (Array.isArray(zipData)) {
                allResults.push(...zipData);
              }
            } catch (error) {
              if (error.name === "AbortError") return;
              console.error("Error fetching zip code results:", error);
            }
          }

          try {
            const autocompleteResponse = await fetch(
              `https://api.locationiq.com/v1/autocomplete?key=${LocationIQApiKey}&q=${encodeURIComponent(
                value,
              )}&limit=5&countrycodes=us`,
              { signal },
            );
            const autocompleteData = await autocompleteResponse.json();
            if (Array.isArray(autocompleteData)) {
              allResults.push(...autocompleteData);
            }
          } catch (error) {
            if (error.name === "AbortError") return;
            console.error("Error fetching autocomplete results:", error);
          }

          const formatted = allResults.map((item) => ({
            place_id: item.place_id,
            display_name: item.display_name,
            lat: item.lat,
            lon: item.lon,
          }));

          const unique = formatted.filter(
            (item, index, self) =>
              index === self.findIndex((t) => t.place_id === item.place_id),
          );

          setSuggestions(unique);
          setShowSuggestions(true);
        } catch (error) {
          if (error.name === "AbortError") return;
          console.error("Error fetching suggestions:", error);
        }
      }, 300); // Wait 300ms after user stops typing
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSelectSuggestion = async (suggestion) => {
    const lat = parseFloat(suggestion.lat);
    const lon = parseFloat(suggestion.lon);

    setLocation(suggestion.display_name);
    updateLatLon(lat, lon);
    setLocationSet(true);
    setLocationMethod("manual");
    setShowSuggestions(false);
    setSuggestions([]);

    const locationLabel = await getLocationLabel(lat, lon);
    posthog.capture("location_set", {
      method: "manual",
      location_city: locationLabel,
    });
  };

  const handleResetLocation = () => {
    setLocation("");
    setLocationSet(false);
    setLocationMethod("");
    setShowSuggestions(false);
    setSuggestions([]);
    setLatitude(null);
    setLongitude(null);
    clearLlFromSessionInfo();
  };

  const handlePriceToggle = (priceLevel) => {
    setSelectedPrices((prev) => {
      const exists = prev.includes(priceLevel);
      const updated = exists
        ? prev.filter((p) => p !== priceLevel)
        : [...prev, priceLevel].sort();

      if (updated.length === 0) {
        setSessionInfo((prevInfo) => ({
          ...(prevInfo || {}),
          min_price: 1,
          max_price: 4,
        }));
        return ["1", "2", "3", "4"];
      } else {
        const priceNumbers = updated.map(Number);
        const minPrice = Math.min(...priceNumbers);
        const maxPrice = Math.max(...priceNumbers);

        setSessionInfo((prevInfo) => ({
          ...(prevInfo || {}),
          min_price: minPrice,
          max_price: maxPrice,
        }));

        posthog.capture("price_range_changed", {
          min_price: minPrice,
          max_price: maxPrice,
          selected_prices: updated,
        });

        return updated;
      }
    });
  };

  const handleOpenNowToggle = (event) => {
    const checked = event.target.checked;
    setOpenNow(checked);
    setSessionInfo((prevInfo) => ({
      ...(prevInfo || {}),
      open_now: checked,
    }));

    posthog.capture("open_now_toggled", {
      open_now: checked,
    });
  };

  const handleSortChange = (sort) => {
    setSortBy(sort);
    setSessionInfo((prev) => ({
      ...(prev || {}),
      sort_by: sort,
    }));

    posthog.capture("sort_changed", {
      sort_by: sort,
    });
  };

  const handleRadiusChange = (_, value) => {
    const miles = Array.isArray(value) ? value[0] : value;
    const milesStr = String(miles);
    setDistance(milesStr);

    const meters = miles * 1610;
    setSessionInfo((prev) => ({
      ...(prev || {}),
      radius: meters,
    }));
  };

  const handlePartySizeChange = (size) => {
    setPartySize(size);
    setSessionInfo((prev) => ({
      ...(prev || {}),
      party_size: size,
    }));

    posthog.capture("party_size_changed", {
      party_size: size,
    });
  };

  const handleExcludeChainsToggle = (event) => {
    const checked = event.target.checked;
    setExcludeChains(checked);
    setSessionInfo((prev) => ({
      ...(prev || {}),
      exclude_all_chains: checked,
    }));

    posthog.capture("exclude_chains_toggled", {
      exclude_chains: checked,
    });
  };

  const toggleCategory = (categoryKey) => {
    setSelectedCategories((prev) => {
      const exists = prev.includes(categoryKey);
      const updated = exists
        ? prev.filter((key) => key !== categoryKey)
        : [...prev, categoryKey];

      const fsqIds =
        updated.length === 0 ? ["13000"] : buildFsqCategoryIds(updated);

      setSessionInfo((prevInfo) => ({
        ...(prevInfo || {}),
        fsq_category_ids: fsqIds,
      }));

      posthog.capture("category_toggled", {
        category: categoryKey,
        action: exists ? "removed" : "added",
        total_categories: updated.length,
      });

      return updated;
    });
  };

  const isFormValid = locationSet;

  const createSession = async (payload) => {
    const res = await authenticatedFetch(`${API_BASE_URL}/sessions`, {
      method: "POST",
      body: JSON.stringify(payload),
    });

    const wrapper = await res.json().catch(() => ({}));

    const data =
      wrapper && typeof wrapper.body === "string"
        ? JSON.parse(wrapper.body)
        : wrapper;

    console.log("Create session raw wrapper:", wrapper);
    console.log("Create session parsed body:", data);

    if (!res.ok || wrapper.statusCode >= 400) {
      throw new Error(data.message || `HTTP ${res.status}`);
    }

    return data;
  };

  const handleNext = async () => {
    if (!isFormValid || isCreating) return;

    try {
      setIsCreating(true);

      // Check if this is demo
      const isDemo = partySize === "1";

      // If demo, set cookie
      if (isDemo) {
        setCookie("foodie_call_demo_seen", "true", 3650); // 10 years
        posthog.capture("demo_started_from_setup");
      }

      const payload = {
        ...(sessionInfo || {}),
        status: isDemo ? "demo" : undefined, // Set status to demo if demo mode
      };
      console.log("Creating session with payload:", payload);

      posthog.capture("session_creation_started", {
        radius_miles: distance,
        party_size: isDemo ? 1 : partySize,
        sort_by: sortBy,
        exclude_chains: excludeChains,
        categories_count: selectedCategories.length,
        has_categories: selectedCategories.length > 0,
        is_demo: isDemo,
      });

      const data = await createSession(payload);

      const sessionId = data.sessionId || data.session_id || data.id;
      if (!sessionId) {
        console.error("No sessionId in response:", data);

        posthog.capture("session_creation_failed", {
          error: "no_session_id",
        });

        alert("Session could not be created.\n\nCheck console for details.");
        return;
      }

      setSessionInfo((prev) => ({
        ...(prev || {}),
        session_id: sessionId,
        joinCode: data.joinCode,
        is_demo: isDemo,
      }));

      posthog.capture("session_created", {
        session_id: sessionId,
        radius_miles: distance,
        party_size: isDemo ? 1 : partySize,
        sort_by: sortBy,
        exclude_chains: excludeChains,
        categories: selectedCategories,
        categories_count: selectedCategories.length,
        is_demo: isDemo,
      });

      navigate(`/session/${sessionId}/lobby`);
    } catch (err) {
      console.error("Error in handleNext:", err);

      posthog.capture("session_creation_error", {
        error: err.message || "unknown",
      });

      alert(`Unable to start session: ${err.message || "Unknown error"}`);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Container>
      <DemoSpotlight buttonPosition={demoButtonPosition} />

      <Header>
        <HeaderRow>
          <HeaderText>
            <HeaderTitle variant="h6" gutterBottom>
              Set Your Preferences
            </HeaderTitle>
            <HeaderSubtitle variant="body2">
              Tell us what you're looking for
            </HeaderSubtitle>
          </HeaderText>

          <NextButton
            variant="contained"
            disabled={!isFormValid || isCreating}
            onClick={handleNext}
            className={isCreating ? "starting" : ""}
          >
            <NextButtonContent>
              {isCreating && (
                <CircularProgress size={14} thickness={5} color="inherit" />
              )}
              <span>{isCreating ? "Starting..." : "Next"}</span>
              {!isCreating && <ArrowRight size={18} />}
            </NextButtonContent>
          </NextButton>
        </HeaderRow>
      </Header>

      <Content>
        {/* Location */}
        <Section>
          <SectionLabel variant="subtitle2">
            <MapPin size={20} color="#f97316" />
            Location
          </SectionLabel>

          {!locationSet ? (
            <>
              <TextField
                fullWidth
                value={location}
                onChange={(e) => handleLocationSearch(e.target.value)}
                placeholder="Enter city, zip code, or address"
                variant="outlined"
                size="medium"
              />

              {showSuggestions && suggestions.length > 0 && (
                <LocationSuggestions elevation={3}>
                  <SuggestionList>
                    {suggestions.map((suggestion, idx) => (
                      <SuggestionItem
                        key={suggestion.place_id || idx}
                        type="button"
                        onClick={() => handleSelectSuggestion(suggestion)}
                      >
                        <SuggestionText>
                          {suggestion.display_name}
                        </SuggestionText>
                      </SuggestionItem>
                    ))}
                  </SuggestionList>
                </LocationSuggestions>
              )}

              <UseCurrentLocationButton
                variant="text"
                onClick={handleUseCurrentLocation}
              >
                📍 Use current location
              </UseCurrentLocationButton>
            </>
          ) : (
            <LocationSetCard>
              <LocationSetRow>
                <LocationSetInfo>
                  <LocationIconWrapper>
                    <MapPin size={20} color="#fff" />
                  </LocationIconWrapper>
                  <Box>
                    <LocationSetTitle variant="subtitle2">
                      Location Set
                    </LocationSetTitle>
                    <LocationSetSubtitle variant="body2">
                      {locationMethod === "current"
                        ? "Using current location"
                        : location}
                    </LocationSetSubtitle>
                  </Box>
                </LocationSetInfo>
              </LocationSetRow>
              <LocationResetButton
                fullWidth
                variant="outlined"
                onClick={handleResetLocation}
              >
                Reset Location
              </LocationResetButton>
            </LocationSetCard>
          )}
        </Section>

        {/* Sort By - Segmented Control */}
        <Section>
          <SectionLabel variant="subtitle2">
            <ArrowUpDown size={20} color="#f97316" />
            Sort By
          </SectionLabel>
          <SegmentedControl>
            {sortOptions.map((option) => (
              <SegmentButton
                key={option.key}
                active={sortBy === option.key}
                onClick={() => handleSortChange(option.key)}
              >
                {option.label}
              </SegmentButton>
            ))}
          </SegmentedControl>
        </Section>

        {/* Distance */}
        <Section>
          <RadiusHeader>
            <RadiusLabel>Search Radius</RadiusLabel>
            <RadiusValue>{distance} miles</RadiusValue>
          </RadiusHeader>
          <Slider
            min={1}
            max={25}
            value={Number(distance)}
            onChange={handleRadiusChange}
          />
          <RadiusLabels>
            <span>1 mi</span>
            <span>25 mi</span>
          </RadiusLabels>
        </Section>

        {/* Party Size - NOW WITH DEMO OPTION */}
        <Section>
          <SectionLabel variant="subtitle2">
            <Users size={20} color="#f97316" />
            Party Size
          </SectionLabel>

          {/* Show demo button only if they haven't seen it */}
          {!hasSeenDemo ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <SegmentedControl>
                {["2", "3", "4"].map((size) => (
                  <SegmentButton
                    key={size}
                    active={partySize === size}
                    onClick={() => handlePartySizeChange(size)}
                  >
                    {size}
                  </SegmentButton>
                ))}
              </SegmentedControl>

              <SegmentedControl>
                <DemoButton
                  ref={demoButtonRef}
                  active={partySize === "1"}
                  onClick={() => handlePartySizeChange("1")}
                >
                  TRY DEMO (1 PERSON)
                </DemoButton>
              </SegmentedControl>
            </Box>
          ) : (
            <SegmentedControl>
              {["2", "3", "4"].map((size) => (
                <SegmentButton
                  key={size}
                  active={partySize === size}
                  onClick={() => handlePartySizeChange(size)}
                >
                  {size}
                </SegmentButton>
              ))}
            </SegmentedControl>
          )}
        </Section>

        {/* Price Range */}
        <Section>
          <SectionLabel variant="subtitle2">
            <DollarSign size={20} color="#f97316" />
            Price Range
          </SectionLabel>
          <PriceGrid>
            {priceOptions.map((price) => {
              const isActive = selectedPrices.includes(price.key);
              return (
                <PriceButton
                  key={price.key}
                  active={isActive}
                  variant={isActive ? "contained" : "outlined"}
                  onClick={() => handlePriceToggle(price.key)}
                >
                  {price.label}
                </PriceButton>
              );
            })}
          </PriceGrid>
        </Section>

        {/* Filters */}
        <Section>
          <ToggleCard>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                width: "100%",
              }}
            >
              <ToggleText>
                <ShopEmoji>🏪</ShopEmoji>
                <Box sx={{ flex: 1 }}>
                  <ToggleTitle>Exclude Chains</ToggleTitle>
                  <ToggleSubtitle variant="body2">
                    Only show local businesses
                  </ToggleSubtitle>
                </Box>
                <Switch
                  checked={excludeChains}
                  onChange={handleExcludeChainsToggle}
                  color="primary"
                />
              </ToggleText>

              <ToggleText>
                <ShopEmoji>🕐</ShopEmoji>
                <Box sx={{ flex: 1 }}>
                  <ToggleTitle>Only Show Open</ToggleTitle>
                  <ToggleSubtitle variant="body2">
                    Filter out closed restaurants
                  </ToggleSubtitle>
                </Box>
                <Switch
                  checked={openNow}
                  onChange={handleOpenNowToggle}
                  color="primary"
                />
              </ToggleText>
            </Box>
          </ToggleCard>
        </Section>

        {/* Food Categories */}
        <Section>
          <FoodCategoriesToggle
            onClick={() => setShowFoodCategories(!showFoodCategories)}
          >
            <FoodCategoriesToggleLeft>
              <ShopEmoji>🍽️</ShopEmoji>
              <Box>
                <FoodCategoriesToggleTitle variant="subtitle2">
                  Food Categories
                </FoodCategoriesToggleTitle>
                <FoodCategoriesToggleSubtitle variant="body2">
                  Optional - Leave empty for all
                </FoodCategoriesToggleSubtitle>
              </Box>
            </FoodCategoriesToggleLeft>
            <FoodCategoriesToggleIcon isOpen={showFoodCategories}>
              <ChevronDown size={20} color="#6b7280" />
            </FoodCategoriesToggleIcon>
          </FoodCategoriesToggle>

          <FoodCategoriesContent isOpen={showFoodCategories}>
            <NoteBox>
              <NoteText variant="body2">
                💡 <strong>Note:</strong> Your partner(s) will also be limited
                to choose restaurants from the categories you select.
              </NoteText>
            </NoteBox>

            <CategoriesGrid>
              {foodCategories.map((category) => {
                const isSelected = selectedCategories.includes(category.key);
                return (
                  <CategoryButton
                    key={category.key}
                    selected={isSelected}
                    variant={isSelected ? "contained" : "outlined"}
                    onClick={() => toggleCategory(category.key)}
                  >
                    <Emoji>{category.emoji}</Emoji>
                    <CategoryLabel variant="body2">
                      {category.label}
                    </CategoryLabel>
                  </CategoryButton>
                );
              })}
            </CategoriesGrid>
          </FoodCategoriesContent>
        </Section>
      </Content>
    </Container>
  );
};

export default Setup;
